import React, { FC, useEffect, useRef, useState } from 'react';
import { Title } from '../developers/page-components/Title';
import { getFormattedDate } from '../../utils/utilities';
import { Spinner } from '../Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { ModalWindow } from '../modal/ModalWindow';
import { fetchBatchData, fetchShelvedBatchesData, fetchUnpostedBatchData } from '../../store/payments/actions/batches';
import { paymentsActionTypes } from '../../store/payments/payments-action-types';
import { useLocation, useParams } from 'react-router-dom';
import { usePaymentContext } from '../../store/payments/payments-context';
import {
	detailsInformationFields,
	getCaptureDate,
	getTotalAmount,
	getValue,
	getValueMark,
} from '../../utils/batchDetailsUtils';
import { IBatchDetailsData, setAdjustment, setComment, setInternalComment } from '../../store/order/actions/batches';
import { AdjustmentModalContent, IAdjustmentBodyProps } from './AdjustmentModalContent';
import { IAdjustmentModalStateProps } from '../../views/batches/BatchesListPage';
import { IBatchesData } from '../../interfaces/funds';
import { BatchDetailsInformationHeader } from './BatchDetailsInformationHeader';
import { BATCHES_TABS } from '../../routing/batchesRoutes';
import { getBatchDetailsPageName } from './BatchDetails';
import { useFormik } from 'formik';
import {
	COMMENT_FORM_FIELD, COMMENT_FORM_FIELD_NAME,
	commentInitialValues,
	convertToInitialCommentValues,
} from '../../constants/comment/comment-form';
import { useDebounce } from 'use-debounce';
import { FormInput } from '../merchants/FormInput';
import { InputType } from '../form/form-input-constants';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';

interface Props {
	fetchDetailsData: (arg: IBatchDetailsData) => Promise<void>;
}
export const BatchDetailsInformation: FC<Props> = ({ fetchDetailsData }) => {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const { id: batchDetailsId } = useParams();
	const isMounted = useRef(false);

	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);

	const locationPathname = getBatchDetailsPageName(pathname, batchDetailsId);

	const { paymentContextState, dispatch } = usePaymentContext();
	const { unpostedBatchesData, batchesData, shelvedBatchesData } = paymentContextState;

	const [localLoading, setLocalLoading] = useState<boolean>(true);

	const [adjustmentModalState, setIsAdjustmentModalState] = useState<IAdjustmentModalStateProps>({
		isOpen: false,
		paymentId: '',
	});

	const formik = useFormik({
		initialValues: commentInitialValues,
		onSubmit: () => void 0,
	});

	const [comment] = useDebounce(formik.values[COMMENT_FORM_FIELD.COMMENT], 1000);
	const [internalComment] = useDebounce(formik.values[COMMENT_FORM_FIELD.INTERNAL_COMMENT], 1000);

	const getBatchDetailsData = (): IBatchesData => {
		switch (locationPathname) {
			case BATCHES_TABS.BATCHES:
				return batchesData?.find(el => el?.payoutId === batchDetailsId);
			case BATCHES_TABS.UNPOSTED_BATCHES:
				return unpostedBatchesData?.find(el => el?.payoutId === batchDetailsId);
			case BATCHES_TABS.SHELVED_BATCHES:
				return shelvedBatchesData?.find(el => el?.payoutId === batchDetailsId);
		}
	};
	console.log('BatchDetailsDataById', getBatchDetailsData());
	const fetchBatchDetailsInformationData = async (): Promise<void> => {
		setLocalLoading(true);

		switch (locationPathname) {
			case BATCHES_TABS.BATCHES:
				await fetchBatchData({ dispatch, params: {}});
				break;
			case BATCHES_TABS.UNPOSTED_BATCHES:
				await fetchUnpostedBatchData({ dispatch, params: {}});
				break;
			case BATCHES_TABS.SHELVED_BATCHES:
				await fetchShelvedBatchesData({ dispatch, params: {}});
		}

		dispatch({ type: paymentsActionTypes.SET_UNPOSTED_BATCHES_DATA_LOADING, payload: false });
		dispatch({ type: paymentsActionTypes.SET_BATCHES_DATA_LOADING, payload: false });
		dispatch({ type: paymentsActionTypes.SET_SHELVED_BATCHES_DATA_LOADING, payload: false });

		setLocalLoading(false);
	};

	useEffect(() => {
		void fetchBatchDetailsInformationData();
	}, []);

	useEffect(() => {
		const convertedData = convertToInitialCommentValues(getBatchDetailsData());
		formik.setValues(convertedData);
	}, [getBatchDetailsData()]);

	useEffect(() => {
		const payoutId = getBatchDetailsData()?.payoutId;
		const value = getBatchDetailsData()?.comment;

		if (payoutId && isMounted.current && comment !== value) {
			void setComment(payoutId, comment);
		}
	}, [comment]);

	useEffect(() => {
		const payoutId = getBatchDetailsData()?.payoutId;
		const value = getBatchDetailsData()?.internalComment;

		if (payoutId && isMounted.current && internalComment !== value) {
			void setInternalComment(payoutId, internalComment);
		}
	}, [internalComment]);

	useEffect(() => {
		if (!isMounted.current) {
			isMounted.current = true;
		}
	}, []);

	const handleAdjustmentSubmit = async (body: IAdjustmentBodyProps): Promise<void> => {
		try {
			await setAdjustment(adjustmentModalState.paymentId, body);
			await fetchUnpostedBatchData({ dispatch, params: {}});
			await fetchDetailsData({ id: batchDetailsId, params: {}});

		} catch (e) {
			console.log(e);
		}
	};

	if (localLoading) {
		return (
			<div className="card h-300px mb-5">
				<div className="card-body">
					<Spinner pageLoader/>
				</div>
			</div>
		);
	}

	if (!getBatchDetailsData()) {
		return (
			<div className="card h-300px mb-5">
				<div className="card-body d-flex align-items-center justify-content-center fs-4">
					No data found.
				</div>
			</div>
		);
	}

	return (
		<div className="card mb-5">
			<div className="card-body">

				<BatchDetailsInformationHeader batchDetailsData={getBatchDetailsData()} />

				<div className="d-flex align-items-center pb-5 mb-5 payout-details-separator">
					<div>
						<Title title="Upcoming payouts" additionalClass="fw-bolder fs-6 me-2" />
						{getCaptureDate(getBatchDetailsData()?.capturePeriodFrom, getBatchDetailsData()?.capturePeriodTo)}

					</div>
					{locationPathname === BATCHES_TABS.BATCHES && (
						<div className="ms-10">
							<Title title="Payout date" additionalClass="fw-bolder fs-6 me-2" />
							<div className="fs-8">
								({getFormattedDate(getBatchDetailsData()?.payoutDate, 'MMM dd, yyyy')})
							</div>
						</div>
					)}
				</div>

				<div className="d-md-flex">
					<div className="col-md-6">
						{detailsInformationFields(getBatchDetailsData()).map(({ fieldName, title, count, value }) => {
							const canChangeAdjustmentFieldValue = fieldName === 'adjustment' && locationPathname === BATCHES_TABS.UNPOSTED_BATCHES;
							// const canChangeReserveFieldValue = fieldName === 'reserve' && unPosted;
							const handleClick = (): void => {
								if (canChangeAdjustmentFieldValue) {
									setIsAdjustmentModalState({ isOpen: true, paymentId: batchDetailsId });
									return;
								}
								// if (canChangeReserveFieldValue) {
								// 	openReserveModal(getBatchDetailsData()?.payoutId);
								// 	return;
								// }
							};

							return (
								<div key={fieldName}>
									<div className="d-flex align-items-center mb-2 fs-6">
										<div className="w-150px">
											{count >= 0 && <span className="me-2">{count}</span>}
											<span>{title}</span>
										</div>
										<div
											// className={clsx((canChangeAdjustmentFieldValue || canChangeReserveFieldValue) && 'cursor-pointer')}
											className={clsx(canChangeAdjustmentFieldValue && 'cursor-pointer')}
											onClick={handleClick}
										>
											{getValueMark(fieldName, value) ? getValueMark(fieldName, value) : <span className="ms-1" />}
											{getBatchDetailsData()?.currencySymbol}{getValue(value)}
											{/*{(canChangeAdjustmentFieldValue || canChangeReserveFieldValue) && <FontAwesomeIcon icon={faPen} className="w-10px text-primary mt-1 ms-2"/>}*/}
											{canChangeAdjustmentFieldValue &&
												<FontAwesomeIcon icon={faPen} className="w-10px text-danger mt-1 ms-2" />}
										</div>
									</div>
								</div>
							);
						})}

					</div>

					<div className="d-none d-md-block col-md-6">
						<Title title={t(COMMENT_FORM_FIELD_NAME.PUBLIC_COMMENT)} additionalClass="fs-6" />
						<div className="fs-8">(This comment will be visible to the merchant when viewing a settlement)</div>
						<FormInput
							formik={formik}
							key={COMMENT_FORM_FIELD.COMMENT}
							fieldName={COMMENT_FORM_FIELD.COMMENT}
							placeholder={t(COMMENT_FORM_FIELD_NAME.COMMENT_PLACEHOLDER)}
							inputType={InputType.Description}
							isEditMode={true}
							additionalClasses="col-12 col-sm-12 col-md-12"
							descriptionFieldHeight={80}
						/>
						{
							checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN) && (
								<FormInput
									formik={formik}
									key={COMMENT_FORM_FIELD.INTERNAL_COMMENT}
									fieldName={COMMENT_FORM_FIELD.INTERNAL_COMMENT}
									label={t(COMMENT_FORM_FIELD_NAME.INTERNAL_COMMENT)}
									placeholder={t(COMMENT_FORM_FIELD_NAME.COMMENT_PLACEHOLDER)}
									inputType={InputType.Description}
									isEditMode={true}
									additionalClasses="col-12 col-sm-8 col-md-12"
									descriptionFieldHeight={80}
								/>
							)
						}
					</div>
				</div>
				<div className="pb-5 mb-5 payout-details-separator"/>

				<div className="d-flex align-items-center justify-content-between">
					<div className="d-flex align-items-center fs-6 fw-bolder">
						<div className="w-150px fs-6 fw-bolder">Total payout</div>
						<div>{getTotalAmount(getBatchDetailsData()?.payoutAmount, getBatchDetailsData()?.currencySymbol)}</div>
					</div>
				</div>

				<div className="d-md-none pb-5 mb-5 payout-details-separator"/>

				<div className="d-md-none">
					<FormInput
						formik={formik}
						key={COMMENT_FORM_FIELD.COMMENT}
						fieldName={COMMENT_FORM_FIELD.COMMENT}
						label={t(COMMENT_FORM_FIELD_NAME.PUBLIC_COMMENT)}
						placeholder={t(COMMENT_FORM_FIELD_NAME.COMMENT_PLACEHOLDER)}
						inputType={InputType.Description}
						isEditMode={true}
						additionalClasses="col-12 col-sm-12 col-md-12"
						descriptionFieldHeight={80}
					/>
					{
						checkRoles(authUser, CLAIMS.SUPER_ADMIN) && (
							<FormInput
								formik={formik}
								key={COMMENT_FORM_FIELD.INTERNAL_COMMENT}
								fieldName={COMMENT_FORM_FIELD.INTERNAL_COMMENT}
								label={t(COMMENT_FORM_FIELD_NAME.INTERNAL_COMMENT)}
								placeholder={t(COMMENT_FORM_FIELD_NAME.COMMENT_PLACEHOLDER)}
								inputType={InputType.Description}
								isEditMode={true}
								additionalClasses="col-12 col-sm-12 col-md-12"
								descriptionFieldHeight={80}
							/>
						)
					}
				</div>

			</div>

			<ModalWindow
				isOpen={adjustmentModalState.isOpen}
				onClose={(): void => setIsAdjustmentModalState({ isOpen: false, paymentId: '' })}
				modalTitle='Adjustment'
				contentClassName='d-block'
				minHeight={185}
				content={
					<AdjustmentModalContent
						closeModal={(): void => setIsAdjustmentModalState({ isOpen: false, paymentId: '' })}
						onFormSubmit={handleAdjustmentSubmit}
					/>
				}
			/>
		</div>
	);
};

